import React, { useState } from 'react';
import styles from './AthleteStats.module.css';
import Overlay from './Overlay';

// Mirror the parent portal's metric conventions so deltas read the same way.
const METRIC_EMOJI = {
  '20yd_dash': '⚡', 'broad_jump': '🦘', 'med_ball': '💥',
  'pushups': '💪', '40yd_dash': '🔥', 'vertical': '🚀',
};
const LOWER_IS_BETTER = new Set(['20yd_dash', '40yd_dash']);

function fmtDate(d) {
  if (!d) return '—';
  const s = typeof d === 'string' ? d : new Date(d).toISOString();
  return new Date(s.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function money(n) { return `$${Number(n || 0).toFixed(2)}`; }
function shortDate(d) {
  if (!d) return '';
  const s = typeof d === 'string' ? d : new Date(d).toISOString();
  return new Date(s.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
// Trim trailing zeros: 3.20 -> "3.2", 132.00 -> "132".
function fmtNum(n) { return (+Number(n).toFixed(2)).toString(); }

// ── Per-metric progress graph (modal) ────────────────────────────────────────
// X-axis is ORDINAL: each assessment is one equal step (labeled by date), so
// uneven date gaps don't distort the shape. Y-axis is the metric value.
function MetricGraph({ metric, onClose }) {
  const vals = metric.values;              // chronological [{ date, value }]
  const n = vals.length;
  const W = 560, H = 320, padL = 54, padR = 28, padT = 26, padB = 58;
  const plotW = W - padL - padR, plotH = H - padT - padB;

  const bool = !!metric.boolean;           // yes/no stat -> fixed 0..1 axis
  const nums = vals.map(v => v.value);
  let lo, hi;
  if (bool) { lo = 0; hi = 1; }
  else { lo = Math.min(...nums); hi = Math.max(...nums); if (lo === hi) { lo -= 1; hi += 1; } }
  const gap = bool ? 0.15 : (hi - lo) * 0.1;
  const yMin = lo - gap, yMax = hi + gap;

  const xAt = (i) => n === 1 ? padL + plotW / 2 : padL + (plotW * i) / (n - 1);
  const yAt = (val) => padT + plotH - ((val - yMin) / (yMax - yMin)) * plotH;
  const pts = vals.map((v, i) => ({ x: xAt(i), y: yAt(v.value), value: v.value, date: v.date }));
  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const yTicks = bool ? [0, 1] : Array.from({ length: 5 }, (_, i) => yMin + ((yMax - yMin) * i) / 4);
  const yLabel = (t) => bool ? (t >= 0.5 ? 'Yes' : 'No') : fmtNum(t);
  const valLabel = (v) => bool ? (v >= 0.5 ? 'Yes' : 'No') : fmtNum(v);
  const lib = !!metric.lowerIsBetter;

  return (
    <Overlay className={styles.graphBackdrop} onClose={onClose}>
      <div className={styles.graphModal} onClick={e => e.stopPropagation()}>
        <div className={styles.graphHeader}>
          <div>
            <div className={styles.graphTitle}>{METRIC_EMOJI[metric.key] || '🏅'} {metric.label}</div>
            <div className={styles.graphSub}>
              {n} assessment{n === 1 ? '' : 's'}{lib ? ' · lower is better' : ''}
              {metric.changePct != null && (
                <span className={metric.improved ? styles.up : styles.down}>
                  {'  '}{metric.changePct >= 0 ? '▲' : '▼'} {Math.abs(metric.changePct).toFixed(1)}%
                </span>
              )}
            </div>
          </div>
          <button className={styles.graphClose} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <svg viewBox={`0 0 ${W} ${H}`} className={styles.graphSvg} role="img" aria-label={`${metric.label} across assessments`}>
          {yTicks.map((t, i) => (
            <g key={i}>
              <line x1={padL} y1={yAt(t)} x2={W - padR} y2={yAt(t)} className={styles.gGrid} />
              <text x={padL - 8} y={yAt(t) + 4} textAnchor="end" className={styles.gYLabel}>{yLabel(t)}</text>
            </g>
          ))}
          {metric.unit && (
            <text x={16} y={padT + plotH / 2} textAnchor="middle"
              transform={`rotate(-90 16 ${padT + plotH / 2})`} className={styles.gAxisTitle}>{metric.unit}</text>
          )}
          {pts.map((p, i) => (
            <text key={i} x={p.x} y={H - padB + 22} textAnchor="middle" className={styles.gXLabel}>{shortDate(p.date)}</text>
          ))}
          {n > 1 && <path d={linePath} className={styles.gLine} />}
          {pts.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="4.5" className={styles.gDot} />
              <text x={p.x} y={p.y - 11} textAnchor="middle" className={styles.gVal}>{valLabel(p.value)}</text>
            </g>
          ))}
        </svg>

        <div className={styles.graphFoot}>Each assessment is one step on the x-axis — the dates aren't to scale.</div>
      </div>
    </Overlay>
  );
}

export default function AthleteStats({ clients, config, showToast, API, authHeader }) {
  const rates = config?.sessionRates || {};
  const sessionLabel = (key) => rates[key]?.label || key || 'Session';

  const allAthletes = (clients || []).flatMap(c =>
    (c.athletes || []).map(a => ({ id: a.id, name: a.name, family: c.name }))
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const [search, setSearch]     = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(false);

  const [editSession, setEditSession] = useState(null); // { id, amount, sessionType, sessionDate, note }
  const [busy, setBusy]               = useState(false);
  const [graphMetric, setGraphMetric] = useState(null); // metric object being graphed | null
  const [showAddStat, setShowAddStat] = useState(false);
  const [newStat, setNewStat]         = useState({ name: '', type: 'integer' });
  const [entryFor, setEntryFor]       = useState(null); // custom stat id being logged to
  const [entryDraft, setEntryDraft]   = useState({ date: '', value: '' });
  const [csBusy, setCsBusy]           = useState(false);

  const q = search.trim().toLowerCase();
  const matches = allAthletes.filter(a => !q || (`${a.name} ${a.family}`).toLowerCase().includes(q));

  const loadStats = async (athleteId) => {
    setSelectedId(athleteId);
    setStats(null);
    setEditSession(null);
    setGraphMetric(null);
    setShowAddStat(false);
    setEntryFor(null);
    setLoading(true);
    try {
      const res = await fetch(`${API}/athletes/${athleteId}/stats`, { headers: authHeader() });
      if (!res.ok) { showToast('Could not load athlete stats', 'error'); return; }
      setStats(await res.json());
    } catch { showToast('Network error', 'error'); }
    finally { setLoading(false); }
  };

  // ── Session edit / delete ────────────────────────────────────────────────────
  const saveSession = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/transactions/${editSession.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          amount: parseFloat(editSession.amount),
          sessionType: editSession.sessionType,
          sessionDate: editSession.sessionDate || null,
          note: editSession.note || null,
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); showToast(e.error || 'Save failed', 'error'); return; }
      showToast('Session updated');
      setEditSession(null);
      loadStats(selectedId);
    } catch { showToast('Network error', 'error'); }
    finally { setBusy(false); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm('Delete this session? This removes the charge and recalculates the family balance.')) return;
    try {
      const res = await fetch(`${API}/transactions/${id}`, { method: 'DELETE', headers: authHeader() });
      if (!res.ok) { showToast('Delete failed', 'error'); return; }
      showToast('Session deleted');
      loadStats(selectedId);
    } catch { showToast('Network error', 'error'); }
  };

  // ── Custom stats ──────────────────────────────────────────────────────────────
  const createCustomStat = async () => {
    if (!newStat.name.trim()) { showToast('Name is required', 'error'); return; }
    setCsBusy(true);
    try {
      const res = await fetch(`${API}/athletes/${selectedId}/custom-stats`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ name: newStat.name.trim(), type: newStat.type }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); showToast(e.error || 'Could not add stat', 'error'); return; }
      showToast('Custom stat added');
      setShowAddStat(false); setNewStat({ name: '', type: 'integer' });
      loadStats(selectedId);
    } catch { showToast('Network error', 'error'); }
    finally { setCsBusy(false); }
  };

  const deleteCustomStat = async (cs) => {
    if (!window.confirm(`Delete "${cs.name}" and all its logged values?`)) return;
    try {
      const res = await fetch(`${API}/custom-stats/${cs.id}`, { method: 'DELETE', headers: authHeader() });
      if (!res.ok) { showToast('Delete failed', 'error'); return; }
      showToast('Custom stat deleted'); loadStats(selectedId);
    } catch { showToast('Network error', 'error'); }
  };

  const openEntry = (cs) => {
    setEntryFor(cs.id);
    setEntryDraft({ date: new Date().toISOString().slice(0, 10), value: cs.type === 'yesno' ? 'yes' : '' });
  };
  const addEntry = async (cs) => {
    if (String(entryDraft.value).trim() === '') { showToast('Value is required', 'error'); return; }
    setCsBusy(true);
    try {
      const res = await fetch(`${API}/custom-stats/${cs.id}/entries`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ date: entryDraft.date || null, value: entryDraft.value }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); showToast(e.error || 'Could not log value', 'error'); return; }
      showToast('Value logged'); setEntryFor(null);
      loadStats(selectedId);
    } catch { showToast('Network error', 'error'); }
    finally { setCsBusy(false); }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      const res = await fetch(`${API}/custom-stat-entries/${entryId}`, { method: 'DELETE', headers: authHeader() });
      if (!res.ok) { showToast('Delete failed', 'error'); return; }
      loadStats(selectedId);
    } catch { showToast('Network error', 'error'); }
  };

  // Shape a custom stat into the MetricGraph's expected form (numeric points).
  const customGraphMetric = (cs) => {
    const lowerIsBetter = cs.type === 'time';
    const numeric = (cs.entries || [])
      .map(e => ({
        date: e.date || (e.createdAt ? String(e.createdAt).slice(0, 10) : ''),
        value: cs.type === 'yesno' ? (e.value === 'yes' ? 1 : 0) : parseFloat(e.value),
      }))
      .filter(v => !isNaN(v.value));
    if (numeric.length === 0) return null;
    const latest = numeric[numeric.length - 1].value;
    const first = numeric[0].value;
    const best = numeric.reduce((b, x) => (lowerIsBetter ? Math.min(b, x.value) : Math.max(b, x.value)), numeric[0].value);
    let changePct = null, improved = null;
    if (cs.type !== 'yesno' && numeric.length >= 2 && first !== 0) {
      changePct = ((latest - first) / Math.abs(first)) * 100;
      improved = lowerIsBetter ? latest < first : latest > first;
    }
    return { key: `custom_${cs.id}`, label: cs.name, unit: '', values: numeric, latest, best, changePct, improved, boolean: cs.type === 'yesno', lowerIsBetter };
  };
  const latestDisplay = (cs) => {
    const e = (cs.entries || [])[(cs.entries || []).length - 1];
    if (!e) return '—';
    return cs.type === 'yesno' ? (e.value === 'yes' ? 'Yes' : 'No') : e.value;
  };
  const TYPE_LABELS = { integer: 'Whole number', decimal: 'Decimal', time: 'Time', yesno: 'Yes / No', text: 'Text' };

  // ── Derived stats ────────────────────────────────────────────────────────────
  const sessions = stats?.sessions || [];
  const effDate = (s) => (s.sessionDate || (s.createdAt ? String(s.createdAt).slice(0, 10) : ''));
  const sessionDates = sessions.map(effDate).filter(Boolean).sort();
  const firstSession = sessionDates[0];
  const lastSession = sessionDates[sessionDates.length - 1];
  const byType = sessions.reduce((acc, s) => { const l = sessionLabel(s.sessionType); acc[l] = (acc[l] || 0) + 1; return acc; }, {});
  let perMonth = null;
  if (sessionDates.length >= 2) {
    const months = Math.max(1, (new Date(lastSession) - new Date(firstSession)) / (1000 * 60 * 60 * 24 * 30.44));
    perMonth = (sessions.length / months).toFixed(1);
  }

  // Assessment progression per metric (oldest → newest).
  const asmChrono = [...(stats?.assessments || [])].reverse();
  const metricMap = {};
  for (const asm of asmChrono) {
    for (const s of (asm.scores || [])) {
      if (s.value == null || s.value === '') continue;
      const v = parseFloat(s.value); if (isNaN(v)) continue;
      if (!metricMap[s.metric_key]) metricMap[s.metric_key] = { key: s.metric_key, label: s.label, unit: s.unit, values: [] };
      metricMap[s.metric_key].values.push({ date: asm.assessment_date, value: v });
    }
  }
  const metrics = Object.values(metricMap).map(m => {
    const lowerIsBetter = LOWER_IS_BETTER.has(m.key);
    const latest = m.values[m.values.length - 1].value;
    const first = m.values[0].value;
    const best = m.values.reduce((b, x) => (lowerIsBetter ? Math.min(b, x.value) : Math.max(b, x.value)), m.values[0].value);
    let changePct = null, improved = null;
    if (m.values.length >= 2 && first !== 0) {
      changePct = ((latest - first) / Math.abs(first)) * 100; // signed by the value's direction
      improved = lowerIsBetter ? latest < first : latest > first;
    }
    return { ...m, lowerIsBetter, latest, best, changePct, improved };
  });

  const selectedAthlete = allAthletes.find(a => a.id === selectedId);

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1 className={styles.pageTitle}>Athlete Statistics</h1>
          <p className={styles.pageSubtitle}>Coach-only view of session activity and assessment progress.</p>
        </div>
      </div>

      <div className={styles.layout}>
        {/* ── Athlete picker ─────────────────────────────────────────────── */}
        <div className={styles.picker}>
          <input
            className={styles.searchInput}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search athletes…"
          />
          <div className={styles.athleteList}>
            {matches.length === 0 ? (
              <div className={styles.athleteEmpty}>No athletes match "{search}".</div>
            ) : matches.map(a => (
              <button
                key={a.id}
                className={`${styles.athleteOption} ${a.id === selectedId ? styles.athleteActive : ''}`}
                onClick={() => loadStats(a.id)}
              >
                <span className={styles.athleteName}>{a.name}</span>
                <span className={styles.athleteFamily}>{a.family}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Detail ─────────────────────────────────────────────────────── */}
        <div className={styles.detail}>
          {!selectedId ? (
            <div className={styles.placeholder}>Select an athlete to see their statistics.</div>
          ) : loading ? (
            <div className={styles.placeholder}>Loading…</div>
          ) : !stats ? (
            <div className={styles.placeholder}>No data.</div>
          ) : (
            <>
              <div className={styles.athleteHeader}>
                <div className={styles.athleteAvatar}>{stats.athlete.name?.charAt(0)}</div>
                <div>
                  <div className={styles.athleteHeaderName}>{stats.athlete.name}</div>
                  <div className={styles.athleteHeaderMeta}>
                    {[stats.athlete.position, stats.athlete.team, stats.athlete.gradYear ? `Class of ${stats.athlete.gradYear}` : null].filter(Boolean).join(' · ') || (selectedAthlete?.family || '')}
                  </div>
                </div>
              </div>

              {/* ── Session Activity ─────────────────────────────────────── */}
              <div className={styles.sectionLabel}>Session Activity</div>
              {sessions.length === 0 ? (
                <div className={styles.emptyCard}>No sessions recorded for this athlete yet.</div>
              ) : (
                <>
                  <div className={styles.statTiles}>
                    <div className={styles.statTile}><div className={styles.statValue}>{sessions.length}</div><div className={styles.statLabel}>Total sessions</div></div>
                    <div className={styles.statTile}><div className={styles.statValue}>{fmtDate(firstSession)}</div><div className={styles.statLabel}>First session</div></div>
                    <div className={styles.statTile}><div className={styles.statValue}>{fmtDate(lastSession)}</div><div className={styles.statLabel}>Last session</div></div>
                    {perMonth && <div className={styles.statTile}><div className={styles.statValue}>{perMonth}</div><div className={styles.statLabel}>Sessions / month</div></div>}
                  </div>
                  <div className={styles.typeChips}>
                    {Object.entries(byType).map(([label, count]) => (
                      <span key={label} className={styles.typeChip}>{label} <strong>×{count}</strong></span>
                    ))}
                  </div>

                  <div className={styles.rows}>
                    {sessions.map(s => editSession?.id === s.id ? (
                      <div key={s.id} className={styles.editRow}>
                        <div className={styles.editGrid}>
                          <label className={styles.editField}><span>Date</span>
                            <input type="date" value={editSession.sessionDate || ''} onChange={e => setEditSession(d => ({ ...d, sessionDate: e.target.value }))} /></label>
                          <label className={styles.editField}><span>Type</span>
                            <select value={editSession.sessionType || ''} onChange={e => setEditSession(d => ({ ...d, sessionType: e.target.value }))}>
                              {Object.entries(rates).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
                              {!rates[editSession.sessionType] && editSession.sessionType && <option value={editSession.sessionType}>{editSession.sessionType}</option>}
                            </select></label>
                          <label className={styles.editField}><span>Amount</span>
                            <input type="number" step="5" value={editSession.amount} onChange={e => setEditSession(d => ({ ...d, amount: e.target.value }))} /></label>
                        </div>
                        <label className={styles.editField}><span>Note</span>
                          <input type="text" value={editSession.note || ''} onChange={e => setEditSession(d => ({ ...d, note: e.target.value }))} placeholder="Optional note" /></label>
                        <div className={styles.editActions}>
                          <button className={styles.cancelBtn} onClick={() => setEditSession(null)}>Cancel</button>
                          <button className={styles.saveBtn} disabled={busy} onClick={saveSession}>{busy ? 'Saving…' : 'Save'}</button>
                        </div>
                      </div>
                    ) : (
                      <div key={s.id} className={styles.row}>
                        <div className={styles.rowMain}>
                          <span className={styles.rowDate}>{fmtDate(effDate(s))}</span>
                          <span className={styles.rowType}>{sessionLabel(s.sessionType)}</span>
                          {s.athleteCount > 1 && <span className={styles.rowSub}>shared · {s.athleteCount} athletes</span>}
                          {s.note && <span className={styles.rowNote}>{s.note}</span>}
                        </div>
                        <div className={styles.rowRight}>
                          <span className={styles.rowAmount}>{money(s.amount)}</span>
                          <button className={styles.linkBtn} onClick={() => setEditSession({ id: s.id, amount: s.amount, sessionType: s.sessionType, sessionDate: s.sessionDate || effDate(s), note: s.note || '' })}>Edit</button>
                          <button className={styles.linkBtnDanger} onClick={() => deleteSession(s.id)}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ── Assessment Progress ──────────────────────────────────── */}
              <div className={styles.sectionLabel}>Assessment Progress</div>
              {(stats.assessments || []).length === 0 ? (
                <div className={styles.emptyCard}>No assessments recorded for this athlete yet.</div>
              ) : (
                <>
                  {metrics.length > 0 && (
                    <div className={styles.metricGrid}>
                      {metrics.map(m => (
                        <button key={m.key} className={styles.metricCard} onClick={() => setGraphMetric(m)} title="Click for graph">
                          <div className={styles.metricHead}>{METRIC_EMOJI[m.key] || '🏅'} {m.label}</div>
                          <div className={styles.metricLatest}>{m.latest}<span className={styles.metricUnit}> {m.unit}</span></div>
                          <div className={styles.metricMeta}>
                            <span>Best: {m.best} {m.unit}</span>
                            {m.changePct != null && (
                              <span className={m.improved ? styles.up : styles.down}>
                                {m.changePct >= 0 ? '▲' : '▼'} {Math.abs(m.changePct).toFixed(1)}%
                              </span>
                            )}
                          </div>
                          <div className={styles.spark}>
                            {m.values.map((v, i) => <span key={i} title={`${fmtDate(v.date)}: ${v.value} ${m.unit}`}>{v.value}</span>)}
                          </div>
                          <div className={styles.metricGraphHint}>View graph →</div>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className={styles.rows}>
                    {(stats.assessments || []).map(asm => (
                      <div key={asm.id} className={styles.asmRow}>
                        <div className={styles.rowMain}>
                          <span className={styles.rowDate}>{fmtDate(asm.assessment_date)}</span>
                          <span className={styles.rowType}>{asm.assessment_type === 'test_day' ? 'Test Day' : 'Evaluation'}</span>
                          <span className={styles.rowSub}>{(asm.scores || []).length} score{(asm.scores || []).length === 1 ? '' : 's'}</span>
                        </div>
                        {(asm.scores || []).length > 0 && (
                          <div className={styles.asmScores}>
                            {(asm.scores || []).map(s => (
                              <span key={s.id} className={styles.asmScoreChip}>
                                {s.label}: <strong>{s.value}{s.unit ? ` ${s.unit}` : ''}</strong>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className={styles.asmHint}>
                    Assessments are created and edited in Client Accounts → Athlete. This view is read-only —
                    use these trends to decide what to change there.
                  </div>
                </>
              )}

              {/* ── Custom Stats ─────────────────────────────────────────── */}
              <div className={styles.customHeader}>
                <div className={styles.sectionLabel}>Custom Stats</div>
                <button className={styles.addCustomBtn} onClick={() => setShowAddStat(v => !v)}>＋ Add Custom Stat</button>
              </div>

              {showAddStat && (
                <div className={styles.addStatForm}>
                  <input
                    className={styles.addStatName}
                    placeholder="Stat name (e.g. GPA, Games played)"
                    value={newStat.name}
                    onChange={e => setNewStat(s => ({ ...s, name: e.target.value }))}
                  />
                  <select className={styles.addStatType} value={newStat.type} onChange={e => setNewStat(s => ({ ...s, type: e.target.value }))}>
                    <option value="integer">Whole number</option>
                    <option value="decimal">Decimal</option>
                    <option value="time">Time (lower is better)</option>
                    <option value="yesno">Yes / No</option>
                    <option value="text">Text (no graph)</option>
                  </select>
                  <button className={styles.cancelBtn} onClick={() => setShowAddStat(false)}>Cancel</button>
                  <button className={styles.saveBtn} disabled={csBusy} onClick={createCustomStat}>{csBusy ? 'Adding…' : 'Add'}</button>
                </div>
              )}

              {(stats.customStats || []).length === 0 ? (
                <div className={styles.emptyCard}>No custom stats yet — add one to track something specific to this athlete.</div>
              ) : (
                <div className={styles.customList}>
                  {(stats.customStats || []).map(cs => {
                    const gm = cs.type !== 'text' ? customGraphMetric(cs) : null;
                    return (
                      <div key={cs.id} className={styles.customCard}>
                        <div className={styles.customTop}>
                          <div className={styles.customMeta}>
                            <span className={styles.customName}>{cs.name}</span>
                            <span className={styles.customTypeBadge}>{TYPE_LABELS[cs.type] || cs.type}</span>
                          </div>
                          <div className={styles.customActions}>
                            <button className={styles.linkBtn} onClick={() => openEntry(cs)}>＋ Log value</button>
                            {gm && <button className={styles.linkBtn} onClick={() => setGraphMetric(gm)}>Graph</button>}
                            <button className={styles.linkBtnDanger} onClick={() => deleteCustomStat(cs)}>Delete</button>
                          </div>
                        </div>
                        <div className={styles.customLatest}>
                          {latestDisplay(cs)}
                          <span className={styles.customLatestSub}> · latest of {(cs.entries || []).length} entr{(cs.entries || []).length === 1 ? 'y' : 'ies'}</span>
                        </div>

                        {entryFor === cs.id && (
                          <div className={styles.entryForm}>
                            <input type="date" value={entryDraft.date} onChange={e => setEntryDraft(d => ({ ...d, date: e.target.value }))} />
                            {cs.type === 'yesno' ? (
                              <select value={entryDraft.value} onChange={e => setEntryDraft(d => ({ ...d, value: e.target.value }))}>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                              </select>
                            ) : cs.type === 'text' ? (
                              <input type="text" placeholder="value" value={entryDraft.value} onChange={e => setEntryDraft(d => ({ ...d, value: e.target.value }))} />
                            ) : (
                              <input type="number" step={cs.type === 'integer' ? '1' : 'any'} placeholder="value" value={entryDraft.value} onChange={e => setEntryDraft(d => ({ ...d, value: e.target.value }))} />
                            )}
                            <button className={styles.cancelBtn} onClick={() => setEntryFor(null)}>Cancel</button>
                            <button className={styles.saveBtn} disabled={csBusy} onClick={() => addEntry(cs)}>{csBusy ? 'Saving…' : 'Log'}</button>
                          </div>
                        )}

                        {(cs.entries || []).length > 0 && (
                          <div className={styles.entryLog}>
                            {[...cs.entries].reverse().map(e => (
                              <div key={e.id} className={styles.entryRow}>
                                <span className={styles.entryDate}>{fmtDate(e.date || (e.createdAt ? String(e.createdAt).slice(0, 10) : ''))}</span>
                                <span className={styles.entryVal}>{cs.type === 'yesno' ? (e.value === 'yes' ? 'Yes' : 'No') : e.value}</span>
                                <button className={styles.entryDel} onClick={() => deleteEntry(e.id)} title="Delete entry">✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {graphMetric && <MetricGraph metric={graphMetric} onClose={() => setGraphMetric(null)} />}
    </div>
  );
}
